import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, Plus, Trash2 } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  createReportSchedule,
  deleteReportSchedule,
  patchReportSchedule,
  useReportSchedules,
} from '@/api/bi';
import type { ReportFormat, ReportFrequency, ReportSchedule } from '@/api/bi';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useFilters, rangeISO } from '@/state/filters';
import { notify } from '@/lib/toast';
import { fmt } from '@/lib/format';

type Draft = {
  name: string;
  page: string;
  frequency: ReportFrequency;
  fmt: ReportFormat;
  recipients: string;
};

const PAGE_OPTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'reports', label: 'Reports (Day Close + Sales)' },
  { id: 'strategic', label: 'Strategic' },
  { id: 'orders', label: 'Orders' },
  { id: 'feedback', label: 'Feedback' },
];

const EMPTY_DRAFT: Draft = {
  name: '',
  page: 'reports',
  frequency: 'daily',
  fmt: 'pdf',
  recipients: '',
};

/**
 * Scheduled email reports — CRUD over `/api/bi/reports/scheduled/`.
 * Used inside the Reports page tabs. Sending is handled out-of-band by
 * a cron job; this UI just stores the intent.
 */
export function ScheduledReportsCard({ canManage = false }: { canManage?: boolean }) {
  const q = useReportSchedules();
  const qc = useQueryClient();
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const storeId = useFilters((s) => s.storeId);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState<number | 'create' | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReportSchedule | null>(null);

  const schedules = q.data?.schedules ?? [];

  async function add() {
    if (!draft.name.trim()) {
      notify.warning('Give it a name first.');
      return;
    }
    setBusy('create');
    try {
      const recipients = draft.recipients
        .split(/[, \n]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      await createReportSchedule({
        name: draft.name.trim(),
        page: draft.page,
        frequency: draft.frequency,
        fmt: draft.fmt,
        recipients,
        filters: { start: iso.start, end: iso.end, store_id: storeId ?? null, day: timeRangeMode },
      });
      setDraft(EMPTY_DRAFT);
      await qc.invalidateQueries({ queryKey: ['bi', 'reports-scheduled'] });
      notify.success('Schedule saved');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save';
      notify.error('Save failed', { description: msg });
    } finally {
      setBusy(null);
    }
  }

  async function toggle(id: number, enabled: boolean) {
    setBusy(id);
    try {
      await patchReportSchedule(id, { enabled });
      await qc.invalidateQueries({ queryKey: ['bi', 'reports-scheduled'] });
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: number) {
    setBusy(id);
    try {
      await deleteReportSchedule(id);
      await qc.invalidateQueries({ queryKey: ['bi', 'reports-scheduled'] });
      notify.message('Schedule deleted', { duration: 1600 });
      setPendingDelete(null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Mail className="h-4 w-4" /> Email digests
        </CardTitle>
        <Badge tone="neutral">{schedules.length} scheduled</Badge>
      </CardHeader>
      <CardBody className="space-y-4 text-sm">
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : q.isError ? (
          <ErrorState onRetry={() => void q.refetch()} />
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={<Mail className="h-5 w-5" />}
            title="No digests scheduled yet"
            description="Add one below to email yourself or your team a recurring snapshot of any BI page."
            withTip
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border bg-surface">
            {schedules.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{s.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {s.page} · {s.frequency} · {s.fmt.toUpperCase()}
                    {s.recipients.length ? ` · ${s.recipients.join(', ')}` : ' · just you'}
                    {s.last_sent_at ? ` · last sent ${fmt.date(s.last_sent_at)}` : ''}
                  </p>
                </div>
                {canManage ? <Button
                  size="sm"
                  variant={s.enabled ? 'primary' : 'outline'}
                  onClick={() => void toggle(s.id, !s.enabled)}
                  disabled={busy === s.id}
                  aria-pressed={s.enabled}
                >
                  {s.enabled ? 'On' : 'Off'}
                </Button> : null}
                {canManage ? <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPendingDelete(s)}
                  disabled={busy === s.id}
                  className="text-muted-foreground hover:text-danger"
                  aria-label={`Delete schedule ${s.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button> : null}
              </li>
            ))}
          </ul>
        )}

        {canManage ? <div className="rounded-md border border-dashed border-border bg-surface px-3 py-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Add new digest
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            <Input
              placeholder="Name (e.g. Daily Day-Close)"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
            <Select
              value={draft.page}
              onValueChange={(v) => setDraft((d) => ({ ...d, page: v }))}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_OPTIONS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={draft.frequency}
              onValueChange={(v) => setDraft((d) => ({ ...d, frequency: v as ReportFrequency }))}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(q.data?.frequencies ?? []).map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={draft.fmt}
              onValueChange={(v) => setDraft((d) => ({ ...d, fmt: v as ReportFormat }))}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(q.data?.formats ?? []).map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="md:col-span-2"
              placeholder="CC emails — comma-separated (optional)"
              value={draft.recipients}
              onChange={(e) => setDraft((d) => ({ ...d, recipients: e.target.value }))}
            />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Sender defaults to your account; we attach the snapshot in the chosen format.
            </p>
            <Button size="sm" variant="primary" onClick={() => void add()} disabled={busy === 'create'}>
              <Plus className="h-3.5 w-3.5" /> Save schedule
            </Button>
          </div>
        </div> : (
          <p className="rounded-md border border-border bg-surface px-3 py-3 text-xs text-muted-foreground">
            You can view schedules, but creating or changing them requires manage access.
          </p>
        )}
      </CardBody>

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(open) => (open ? null : setPendingDelete(null))}
        title="Delete this digest?"
        description="The schedule stops immediately and nobody on the recipient list receives it again."
        details={
          pendingDelete ? (
            <span>
              <span className="font-medium">{pendingDelete.name}</span> · {pendingDelete.frequency} ·{' '}
              {pendingDelete.recipients.length
                ? pendingDelete.recipients.join(', ')
                : 'just you'}
            </span>
          ) : null
        }
        confirmLabel="Delete digest"
        tone="destructive"
        isPending={pendingDelete != null && busy === pendingDelete.id}
        onConfirm={() => (pendingDelete ? void remove(pendingDelete.id) : undefined)}
      />
    </Card>
  );
}
